import React, { useEffect, useState } from "react";
import { enviroment } from "../../../../enviroment";
import {
  getContainedImageRect,
  RectLike,
} from "../functions/paintedImageRect";

// Flag REACT_APP_LOGO_CLONE_SIZE_FIX - build-time constant (same measure
// as LogoRuler: the painted bitmap under the flag, the element box without).
const sizeFixEnabled = enviroment.logoCloneSizeFixEnabled;

function rectsEqual(a: RectLike | null, b: RectLike): boolean {
  return (
    !!a &&
    a.top === b.top &&
    a.left === b.left &&
    a.width === b.width &&
    a.height === b.height
  );
}

/**
 * Flag REACT_APP_LOGO_DRAG_RESIZE: where the canvas bitmap is painted,
 * RELATIVE to the editor container (so an absolutely positioned handle can
 * sit on its corner). Same no-deps/observer idiom as LogoRuler: the img can
 * be replaced or load its high-res after mount, so every render
 * re-subscribes to the current elements; the equality guard keeps the state
 * stable. getBoundingClientRect includes the optimistic-preview transform;
 * transitionend re-measures once a 120 ms scale transition has settled.
 */
export function usePaintedImageRect(
  containerRef: React.RefObject<HTMLElement>,
  logoRef: React.RefObject<HTMLImageElement>
): RectLike | null {
  const [rect, setRect] = useState<RectLike | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    const img = logoRef.current;
    if (!container || !img) return;

    const update = () => {
      const cr = container.getBoundingClientRect();
      const er = img.getBoundingClientRect();
      const lr = sizeFixEnabled ? getContainedImageRect(er, img) : er;
      const next: RectLike = {
        left: lr.left - cr.left,
        top: lr.top - cr.top,
        width: lr.width,
        height: lr.height,
      };
      setRect((prev) => (rectsEqual(prev, next) ? prev : next));
    };

    update();

    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
    observer?.observe(container);
    observer?.observe(img);
    img.addEventListener("load", update);
    img.addEventListener("transitionend", update);
    window.addEventListener("resize", update);

    return () => {
      observer?.disconnect();
      img.removeEventListener("load", update);
      img.removeEventListener("transitionend", update);
      window.removeEventListener("resize", update);
    };
  });

  return rect;
}
